import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Tooltip,
} from "recharts";

interface DayBP {
  day: number;
  systolic: number | null;
  diastolic: number | null;
}

interface MonthlyBloodPressureChartProps {
  data?: DayBP[];
  daysInMonth?: number;
}

const buildDefault = (n: number): DayBP[] =>
  Array.from({ length: n }, (_, i) => ({
    day: i + 1,
    systolic: 110 + Math.round(Math.sin(i * 0.5) * 10) + (i % 3),
    diastolic: 75 + Math.round(Math.cos(i * 0.6) * 6),
  }));

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-[6px] px-3 py-2 shadow text-xs font-inter">
      <p className="font-semibold text-[#1F1F1F] mb-1">{label}</p>
      {payload.map((p: any) => (
        <div key={p.name} className="flex items-center gap-1.5 mt-0.5">
          <span
            className="w-2 h-2 rounded-full inline-block flex-shrink-0"
            style={{ background: p.color }}
          />
          <span style={{ color: p.color }} className="font-medium">
            {p.name === "systolic" ? "Systolic" : "Diastolic"} - {p.value}
          </span>
        </div>
      ))}
    </div>
  );
};

function computeBpYAxis(data: DayBP[]): { domain: [number, number]; ticks: number[] } {
  const values: number[] = [];
  data.forEach((d) => {
    if (d.systolic != null) values.push(d.systolic);
    if (d.diastolic != null) values.push(d.diastolic);
  });
  if (values.length === 0) {
    return { domain: [60, 140], ticks: [60, 80, 100, 120, 140] };
  }
  const rawMin = Math.min(...values);
  const rawMax = Math.max(...values);
  const span = Math.max(rawMax - rawMin, 40);
  const rawStep = span / 4;
  const step = Math.max(10, Math.ceil(rawStep / 10) * 10);
  const min = Math.floor(rawMin / step) * step;
  const max = Math.ceil(rawMax / step) * step;
  const domainMax = max === min ? min + step : max;
  const ticks: number[] = [];
  for (let v = min; v <= domainMax; v += step) ticks.push(v);
  return { domain: [min, domainMax], ticks };
}

const MonthlyBloodPressureChart: React.FC<MonthlyBloodPressureChartProps> = ({
  data,
  daysInMonth = 30,
}) => {
  const chartData = data ?? buildDefault(daysInMonth);
  const { domain, ticks } = computeBpYAxis(chartData);

  return (
    <div className="bg-white border border-[#F3F4F6] rounded-xl p-4 flex flex-col h-full">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-baseline gap-1">
          <span className="text-[12px] 3xl:text-[14px] font-semibold uppercase tracking-widest text-[#1F1F1F] font-inter">
            Blood Pressure
          </span>
          <span className="text-[10px] 3xl:text-[12px] text-[#8D8D8D] font-inter">(mmHg)</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-[#4CB964] inline-block" />
            <span className="text-[10px] 3xl:text-[12px] text-[#1F1F1F] font-inter">Systolic</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-[#FF9F7A] inline-block" />
            <span className="text-[10px] 3xl:text-[12px] text-[#1F1F1F] font-inter">Diastolic</span>
          </div>
        </div>
      </div>

      <div className="flex-1">
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={chartData} margin={{ top: 12, right: 12, left: -12, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke="#F3F4F6" />
            <Tooltip content={<CustomTooltip />} cursor={{ stroke: "#E5E7EB", strokeDasharray: "3 3" }} />
            <XAxis
              dataKey="day"
              axisLine={false}
              tickLine={false}
              interval={0}
              tick={{ fontSize: 10, fontFamily: "Inter, sans-serif", fill: "#8D8D8D" }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 11, fontFamily: "Inter, sans-serif", fill: "#8D8D8D" }}
              domain={domain}
              ticks={ticks}
            />
            <Line
              type="monotone"
              dataKey="systolic"
              stroke="#4CB964"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
            <Line
              type="monotone"
              dataKey="diastolic"
              stroke="#FF9F7A"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default MonthlyBloodPressureChart;
